/**
 * Contextual Help System for RepConnect
 * Provides tooltips, guided tours, and contextual assistance for healthcare providers
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 */

import { UserStorage } from './userStorage';
import { logger } from './logger';

export class ContextualHelp {
  /**
   * Get help content for different UI elements
   */
  static getHelpContent() {
    return {
      dashboard: {
        customization: {
          title: "Dashboard Customization",
          content: "Click 'Customize' to rearrange dashboard sections. Select a section, then click another to move it. Use eye icons to show/hide sections.",
          position: "bottom"
        },
        statCards: {
          title: "Meeting Statistics",
          content: "These cards show your meeting metrics. Orange cards indicate items needing attention, blue cards show upcoming events.",
          position: "bottom"
        },
        bulkActions: {
          title: "Bulk Actions",
          content: "Select multiple meeting requests using checkboxes, then use the toolbar to approve or decline them all at once.",
          position: "top"
        },
        smartSuggestions: {
          title: "Smart Suggestions",
          content: "AI-powered recommendations based on your past meeting patterns and preferences. Click 'Smart Response' for pre-written templates.",
          position: "top"
        }
      },
      
      meetingRequests: {
        approvedRep: {
          title: "Approved Representatives",
          content: "Green badges indicate sales reps you've previously approved. These requests can often be auto-approved based on your preferences.",
          position: "right"
        },
        urgentRequests: {
          title: "Urgent Requests",
          content: "Red indicators show meetings scheduled within 48 hours that need immediate attention.",
          position: "left"
        },
        smartResponse: {
          title: "Smart Response Templates",
          content: "Choose from AI-generated response templates based on your communication style and meeting history. Edit before sending.",
          position: "top"
        },
        timePreferences: {
          title: "Your Preferred Times",
          content: "Based on your meeting history, these are the times you typically prefer for lunch meetings.",
          position: "bottom"
        }
      },

      quickActions: {
        meetingManagement: {
          title: "Meeting Management",
          content: "Quick access to review pending requests and manage your meeting schedule.",
          position: "right"
        },
        profileSettings: {
          title: "Profile Settings",
          content: "Manage your availability, dining preferences, and communication settings.",
          position: "right"
        }
      }
    };
  }

  /**
   * Get guided tour steps for new users
   */
  static getGuidedTour(userType = 'customer') {
    const tours = {
      customer: [
        {
          target: '[data-tour="welcome"]',
          title: "Welcome to RepConnect!",
          content: "Let's take a quick tour of your healthcare provider dashboard. This will help you get started with managing meeting requests efficiently.",
          placement: "center"
        },
        {
          target: '[data-tour="stat-cards"]',
          title: "Your Meeting Overview",
          content: "These cards show your meeting statistics. The orange 'Meeting Requests' card shows pending requests that need your attention.",
          placement: "bottom"
        },
        {
          target: '[data-tour="meeting-requests"]',
          title: "Meeting Requests",
          content: "Here you'll see requests from sales representatives. Each card shows the meeting details and rep information.",
          placement: "top"
        },
        {
          target: '[data-tour="smart-suggestions"]',
          title: "Smart Suggestions",
          content: "Our AI analyzes your preferences to provide intelligent recommendations and pre-written response templates.",
          placement: "bottom"
        },
        {
          target: '[data-tour="bulk-actions"]',
          title: "Bulk Actions",
          content: "Select multiple requests using checkboxes to approve or decline several meetings at once - great for busy schedules!",
          placement: "top"
        },
        {
          target: '[data-tour="customize-button"]',
          title: "Customize Your Dashboard",
          content: "Click here to rearrange dashboard sections to match your workflow. You can also hide sections you don't need.",
          placement: "left"
        },
        {
          target: '[data-tour="quick-actions"]',
          title: "Quick Actions",
          content: "Access frequently used features quickly. Update your availability, manage preferences, or review all requests.",
          placement: "top"
        }
      ],

      sales: [
        {
          target: '[data-tour="welcome"]',
          title: "Welcome Sales Professional!",
          content: "Your dashboard helps you manage meeting requests and track your interactions with healthcare providers.",
          placement: "center"
        },
        {
          target: '[data-tour="stat-cards"]',
          title: "Sales Metrics",
          content: "Track your scheduled meetings, pending requests, spending, and provider relationships at a glance.",
          placement: "bottom"
        },
        {
          target: '[data-tour="create-request"]',
          title: "Create Meeting Requests",
          content: "Click here to send meeting requests to healthcare providers. The system will guide you through the process.",
          placement: "bottom"
        }
      ]
    };

    return tours[userType] || tours.customer;
  }

  /**
   * Get contextual tips based on user actions and current state
   */
  static getContextualTips(context) {
    const tips = {
      firstLogin: {
        type: "welcome",
        title: "Welcome to RepConnect!",
        message: "We've detected this is your first time here. Would you like a quick tour to get started?",
        actions: [
          { label: "Take Tour", action: "startTour" },
          { label: "Skip", action: "dismiss" }
        ]
      },

      manyPendingRequests: {
        type: "efficiency",
        title: "Multiple Requests Detected",
        message: "You have several pending meeting requests. Use bulk actions to handle multiple requests efficiently.",
        actions: [
          { label: "Show Me How", action: "highlightBulkActions" },
          { label: "Got It", action: "dismiss" }
        ]
      },

      approvedRepRequest: {
        type: "suggestion",
        title: "Trusted Representative",
        message: "This request is from a rep you've previously approved. Consider using smart templates for quick responses.",
        actions: [
          { label: "Use Smart Response", action: "showSmartResponse" },
          { label: "Dismiss", action: "dismiss" }
        ]
      },

      urgentRequest: {
        type: "urgent",
        title: "Time-Sensitive Request",
        message: "This meeting is scheduled within 48 hours. Consider responding quickly to avoid scheduling conflicts.",
        actions: [
          { label: "Respond Now", action: "focusResponse" },
          { label: "Remind Me Later", action: "snooze" }
        ]
      },

      noDashboardCustomization: {
        type: "feature",
        title: "Personalize Your Dashboard",
        message: "Did you know you can customize your dashboard? Rearrange sections to match your workflow.",
        actions: [
          { label: "Show Me", action: "highlightCustomization" },
          { label: "Not Now", action: "dismiss" }
        ]
      }
    };

    return tips[context] || null;
  }

  /**
   * Get feature explanations for specific UI elements
   */
  static getFeatureExplanations() {
    return {
      customizationMode: {
        title: "Dashboard Customization",
        steps: [
          "Click a section to select it (you'll see a blue highlight)",
          "Click another section to move the selected section there",
          "Use the eye icons to show/hide sections",
          "Your changes are saved automatically"
        ]
      },

      bulkActions: {
        title: "Bulk Actions Guide",
        steps: [
          "Check the boxes next to meeting requests you want to handle",
          "Use 'Select All' to choose all visible requests",
          "Click the bulk action toolbar that appears",
          "Choose 'Approve All' or 'Decline All' to process multiple requests"
        ]
      },

      smartResponse: {
        title: "Smart Response System",
        steps: [
          "Click 'Smart Response' on any meeting request",
          "Choose from pre-written templates based on your history",
          "Edit the message to personalize it",
          "Click 'Send & Approve' or 'Send & Decline' to respond"
        ]
      }
    };
  }

  /**
   * Check if user needs contextual help based on their usage patterns
   */
  static shouldShowHelp(userId, context) {
    const user = UserStorage.getUserById(userId);
    if (!user) return false;

    const helpHistory = this.getHelpHistory(userId);
    const accountAge = this.getAccountAge(user);
    
    // Show help for new users (less than 7 days)
    if (accountAge < 7 && !helpHistory.tourCompleted) {
      return { type: 'tour', reason: 'new_user' };
    }

    // Show contextual tips based on usage patterns
    switch (context) {
      case 'multiplePendingRequests':
        return !helpHistory.dismissedBulkActions && { type: 'tip', reason: 'efficiency' };
      
      case 'approvedRepRequest':
        return !helpHistory.usedSmartResponse && { type: 'tip', reason: 'feature_suggestion' };
      
      case 'dashboardVisit':
        return !helpHistory.customizedDashboard && accountAge > 3 && { type: 'tip', reason: 'personalization' };
      
      default:
        return false;
    }
  }

  /**
   * Get user's help interaction history
   */
  static getHelpHistory(userId) {
    try {
      const history = localStorage.getItem(`help_history_${userId}`);
      return history ? JSON.parse(history) : {
        tourCompleted: false,
        dismissedBulkActions: false,
        usedSmartResponse: false,
        customizedDashboard: false,
        lastHelpShown: null
      };
    } catch (error) {
      logger.error('Error loading help history:', error);
      return {};
    }
  }

  /**
   * Update user's help interaction history
   */
  static updateHelpHistory(userId, updates) {
    try {
      const current = this.getHelpHistory(userId);
      const updated = { ...current, ...updates, lastUpdated: new Date().toISOString() };
      localStorage.setItem(`help_history_${userId}`, JSON.stringify(updated));
    } catch (error) {
      logger.error('Error updating help history:', error);
    }
  }

  /**
   * Calculate account age in days
   */
  static getAccountAge(user) {
    if (!user.createdAt) return 0;
    const created = new Date(user.createdAt);
    const now = new Date();
    return Math.floor((now - created) / (1000 * 60 * 60 * 24));
  }

  /**
   * Get keyboard shortcuts help
   */
  static getKeyboardShortcuts() {
    return {
      global: [
        { key: "?", description: "Show/hide help" },
        { key: "Ctrl + /", description: "Search" },
        { key: "Esc", description: "Close modals/cancel actions" }
      ],
      dashboard: [
        { key: "c", description: "Toggle customization mode" },
        { key: "r", description: "Refresh data" },
        { key: "n", description: "Go to meeting requests" }
      ],
      meetingRequests: [
        { key: "a", description: "Approve selected request" },
        { key: "d", description: "Decline selected request" },
        { key: "s", description: "Toggle smart response" },
        { key: "Ctrl + a", description: "Select all requests" }
      ]
    };
  }
}

export default ContextualHelp;